import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Column } from "primereact/column";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import FamilyApi from "../../services/FamilyApi";

// Design System
import DataTable from "../../components/DataTable/DataTable";
import FilterPanel from "../../components/FilterPanel/FilterPanel";
import Input from "../../components/Input/Input";
import { Button } from "../../components/Button/Button";
import { IconButton } from "../../components/IconButton/IconButton";
import PageHeader from "../../components/PageHeader/PageHeader";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import type { PaginatorPageChangeEvent } from "../../components/Paginator/Paginator";

const STORAGE_KEY = "ListFamilies_filters";

interface Family {
  id: string;
  name: string;
  description: string;
  code: string;
}

const ListFamilies: React.FC = () => {
  const { plantId } = useAuth();
  const [families, setFamilies] = useState<Family[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { t: translateForm } = useTranslation("form");
  const { t: translateGeneral } = useTranslation("general");
  const navigate = useNavigate();
  const { displayToast } = useToast();

  const [filterCode, setFilterCode] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterDescription, setFilterDescription] = useState("");

  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [familyToDelete, setFamilyToDelete] = useState<string | null>(null);

  const [savedFilters, setSavedFilters] = useState<Record<string, any>>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    if (!plantId) return;
    fetchFamilies(savedFilters, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantId]);

  const fetchFamilies = async (
    filters: Record<string, any> = {},
    firstIndex = 0,
    field = sortField,
    direction: "asc" | "desc" = sortOrder,
    pageSize: number = rows,
  ): Promise<void> => {
    setIsLoading(true);
    try {
      const name = filters.name?.value || undefined;
      const code = filters.code?.value || undefined;
      const description = filters.description?.value || undefined;

      const response = await FamilyApi.GetFamiliesByFilterAsync(
        [plantId],
        Math.floor(firstIndex / pageSize) + 1,
        pageSize,
        field,
        direction,
        code,
        name,
        description,
      );

      const data = Array.isArray(response.data?.items)
        ? response.data.items
        : Array.isArray(response.data?.families)
          ? response.data.families
          : [];

      setFamilies(data);
      setTotalRecords(response.data.total || data.length);
    } catch (err) {
      displayToast("error", translateForm("error"), "Erro ao buscar famílias", false, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilter = (): void => {
    const filters: Record<string, any> = {
      code: filterCode ? { value: filterCode } : undefined,
      name: filterName ? { value: filterName } : undefined,
      description: filterDescription ? { value: filterDescription } : undefined,
    };

    Object.keys(filters).forEach((key) => {
      if (filters[key] === undefined) delete filters[key];
    });

    setPage(0);
    setSavedFilters(filters);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    fetchFamilies(filters, 0);
  };

  const handleClearFilters = (): void => {
    setFilterCode("");
    setFilterName("");
    setFilterDescription("");
    setPage(0);
    setSavedFilters({});
    localStorage.removeItem(STORAGE_KEY);
    fetchFamilies({}, 0);
  };

  const handlePage = (e: PaginatorPageChangeEvent): void => {
    setPage(e.first);
    setRows(e.rows);
    fetchFamilies(savedFilters, e.first, sortField, sortOrder, e.rows);
  };

  const onSort = (e: any): void => {
    setSortField(e.sortField);
    setSortOrder(e.sortOrder === 1 ? "asc" : "desc");
    fetchFamilies(savedFilters, page, e.sortField, e.sortOrder === 1 ? "asc" : "desc");
  };

  const deleteFamily = async (id: string): Promise<void> => {
    try {
      await FamilyApi.DeleteFamily(id);
      setFamilies((prev) => prev.filter((f) => f.id !== id));
      displayToast("success", translateForm("familySuceffullyDeleted"), "", false, 3000);
    } catch (err: any) {
      const errorMessage = err?.data?.data || translateForm("familyNotDeleted");
      displayToast("error", translateForm("error"), errorMessage, false, 3000);
    }
  };

  const confirmDelete = (id: string): void => {
    setFamilyToDelete(id);
    setShowConfirmDelete(true);
  };

  const truncateWithTooltip = (text: string, limit: number = 30): React.ReactNode => {
    if (!text) return "";
    const truncated = text.length > limit ? text.slice(0, limit) + "..." : text;
    return <span title={text}>{truncated}</span>;
  };

  const actionBodyTemplate = (rowData: Family): JSX.Element => (
    <div style={{ display: "flex", gap: "0.5rem" }}>
      <IconButton
        icon="visibility"
        variant="text"
        severity="secondary"
        onClick={() => navigate(`/families/${rowData.id}`)}
        aria-label={translateGeneral("seeDetails")}
      />
      <IconButton
        icon="edit"
        variant="text"
        severity="secondary"
        onClick={() => navigate(`/families/${rowData.id}/edit`)}
        aria-label={translateGeneral("edit")}
      />
      <IconButton
        icon="delete"
        variant="text"
        severity="secondary"
        onClick={() => confirmDelete(rowData.id)}
        aria-label={translateGeneral("delete")}
      />
    </div>
  );

  return (
    <div className="overflow-hidden">
      <PageHeader
        title={translateForm("families")}
        description="Gerencie as categorias e famílias do sistema"
      >
        <Button
          label={translateForm("register")}
          icon="add"
          severity="primary"
          onClick={() => navigate("/families/create")}
        />
      </PageHeader>

      <div>
        <ConfirmModal
          visible={showConfirmDelete}
          onHide={() => {
            setShowConfirmDelete(false);
            setFamilyToDelete(null);
          }}
          onConfirm={() => familyToDelete && deleteFamily(familyToDelete)}
          title={translateGeneral("confirmation")}
          message={translateForm("familyDeleteConfirmMessage")}
          severity="danger"
          confirmLabel={translateForm("Yes")}
          cancelLabel={translateForm("No")}
        />
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <FilterPanel
            title="Filtros"
            showFilterIcon
            onApply={handleFilter}
            onClear={handleClearFilters}
          >
            <Input
              type="text"
              label={translateForm("code")}
              placeholder="Digite o código"
              value={filterCode}
              onChange={(val: any) => setFilterCode(val)}
            />
            <Input
              type="text"
              label={translateForm("name")}
              placeholder="Digite o nome"
              value={filterName}
              onChange={(val: any) => setFilterName(val)}
            />
            <Input
              type="text"
              label={translateForm("description")}
              placeholder="Digite a descrição"
              value={filterDescription}
              onChange={(val: any) => setFilterDescription(val)}
            />
          </FilterPanel>

          <DataTable
            value={families}
            paginated
            first={page}
            rows={rows}
            totalRecords={totalRecords}
            onPageChange={handlePage}
            rowsPerPageOptions={[10, 20, 50]}
            loading={isLoading}
            emptyMessage={translateForm("noRecordsFound") || "Nenhum registro encontrado"}
            sortField={sortField}
            sortOrder={sortOrder === "asc" ? 1 : -1}
            onSort={onSort}
            resizableColumns
          >
            <Column
              field="code"
              header={translateForm("code")}
              sortable
              style={{ minWidth: "8rem" }}
            />
            <Column
              field="name"
              header={translateForm("name")}
              sortable
              body={(rowData: Family) => truncateWithTooltip(rowData.name)}
            />
            <Column
              field="description"
              header={translateForm("description")}
              sortable
              body={(rowData: Family) => truncateWithTooltip(rowData.description)}
            />
            <Column
              field="actions"
              header={translateForm("actions")}
              body={actionBodyTemplate}
              style={{ width: "7rem" }}
              resizeable={false}
            />
          </DataTable>
        </div>
      </div>
    </div>
  );
};

export default ListFamilies;
